import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  StreamVideo,
  StreamCall,
  LivestreamLayout,
} from "@stream-io/video-react-sdk";
import "@stream-io/video-react-sdk/dist/css/styles.css";
import { Chat, Channel, MessageList, MessageComposer } from "stream-chat-react";
import "stream-chat-react/dist/css/index.css";
import { ArrowLeft, Gift, Heart, Users, Images, ChartBar, Target, Plus, Medal } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { getStreamClients } from "../lib/streamClient";
import GiftLeaderboardBanner from "../components/GiftLeaderboardBanner";
import GiftBattleOverlay from "../components/GiftBattleOverlay";
import LiveChatMessage from "../components/LiveChatMessage";
import { frameStyle } from "../lib/badgeFrames";
import LiveGiftSplash from "../components/LiveGiftSplash";
import PkBattleBar from "../components/PkBattleBar";
import TopGifterEntrance from "../components/TopGifterEntrance";
import TitlesSheet from "../components/TitlesSheet";
import { useAuth } from "../context/AuthContext";

export default function LiveViewer() {
  const { user: me } = useAuth();
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [videoClient, setVideoClient] = useState(null);
  const [chatClient, setChatClient] = useState(null);
  const [call, setCall] = useState(null);
  const [channel, setChannel] = useState(null);
  const [giftOpen, setGiftOpen] = useState(false);
  const [gifts, setGifts] = useState([]);
  const [recentGift, setRecentGift] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [topViewers, setTopViewers] = useState([]);
  const [viewersOpen, setViewersOpen] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [waveBusy, setWaveBusy] = useState(false);
  const [rank, setRank] = useState(null);
  const [goal, setGoal] = useState(null);
  const [goalOpen, setGoalOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryPosts, setGalleryPosts] = useState([]);
  const [contribOpen, setContribOpen] = useState(false);
  const [contrib, setContrib] = useState(null);
  const [titlesOpen, setTitlesOpen] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [multiplier, setMultiplier] = useState(1);
  const callRef = useRef(null);
  const fmt = (n) => (n || 0).toLocaleString();

  // League color by letter (A gold · B violet · C sky · D slate).
  const leagueStyle = (label) => {
    const L = (label || "D")[0];
    if (L === "A") return "bg-jam-gold/20 text-jam-gold border-jam-gold/40";
    if (L === "B") return "bg-violet-500/20 text-violet-300 border-violet-400/40";
    if (L === "C") return "bg-papaya/20 text-papaya border-papaya/40";
    return "bg-white/10 text-cream/70 border-white/20";
  };

  // Real viewer presence + live count/top-viewers polling (Phase 1)
  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    const refresh = async () => {
      try {
        const { data } = await api.get(`/live/${sessionId}/viewers`);
        if (alive) { setViewerCount(data.count); setTopViewers(data.top || []); }
      } catch { /* ignore */ }
    };
    api.post(`/live/${sessionId}/join`).then((r) => alive && setViewerCount(r.data.count)).catch(() => {});
    refresh();
    const hb = setInterval(() => api.post(`/live/${sessionId}/heartbeat`).catch(() => {}), 10000);
    const poll = setInterval(refresh, 3000);
    return () => { alive = false; clearInterval(hb); clearInterval(poll); api.post(`/live/${sessionId}/leave`).catch(() => {}); };
  }, [sessionId]);

  // Host follow state
  useEffect(() => {
    if (!session?.host_username) return;
    api.get(`/users/${session.host_username}`)
      .then((r) => { setFollowing(!!r.data.is_following); setFollowers(r.data.followers_count || 0); })
      .catch(() => {});
  }, [session?.host_username]);

  // LIVE engagement rank + league (Phase 2) — lighter cadence than viewer poll.
  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    const load = () => api.get(`/live/${sessionId}/rank`)
      .then((r) => { if (alive) setRank(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 12000);
    return () => { alive = false; clearInterval(t); };
  }, [sessionId]);

  // Gift Goal poll (Phase 3)
  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    const load = () => api.get(`/live/${sessionId}/gift-goal`)
      .then((r) => { if (alive) setGoal(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [sessionId]);

  // Gallery = host's recent posts (loaded on demand)
  useEffect(() => {
    if (!galleryOpen || !session?.host_username) return;
    api.get(`/users/${session.host_username}/posts`)
      .then((r) => setGalleryPosts(r.data || []))
      .catch(() => setGalleryPosts([]));
  }, [galleryOpen, session?.host_username]);

  // My Contribution (loaded on demand + after a send)
  const loadContrib = () => api.get(`/live/${sessionId}/my-contribution`)
    .then((r) => setContrib(r.data)).catch(() => {});
  useEffect(() => {
    if (contribOpen) loadContrib();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contribOpen]);

  // Gift-Goal gallery (loaded when the goal sheet opens)
  useEffect(() => {
    if (!goalOpen) return;
    api.get(`/live/${sessionId}/gift-goal/gallery`).then((r) => setGallery(r.data.goals || [])).catch(() => setGallery([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalOpen]);

  const followHost = async () => {
    if (following) return;
    try {
      const { data } = await api.post(`/live/${sessionId}/follow`);
      setFollowing(true); setFollowers(data.followers_count);
      toast.success(`Following @${session.host_username}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't follow"); }
  };

  const sendWave = async () => {
    setWaveBusy(true);
    try {
      const txn = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      await api.post(`/live/${sessionId}/wave`, { amount: 1, txn_id: txn });
      toast.success("Sent 1 WAVE ❤️");
      const { data } = await api.get(`/live/${sessionId}/viewers`);
      setViewerCount(data.count); setTopViewers(data.top || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send"); }
    finally { setWaveBusy(false); }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      // Session + gift catalog first (these do not depend on Stream A/V).
      let s;
      try {
        const [{ data }, catalog] = await Promise.all([
          api.get(`/live/${sessionId}`),
          api.get("/gifts/catalog"),
        ]);
        if (!mounted) return;
        s = data;
        setSession(data);
        setGifts(catalog.data.gifts || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "This stream is no longer available");
        nav("/live");
        return;
      }
      // Stream A/V is best-effort: if it can't join (preview / no broadcaster yet),
      // keep the full LIVE experience running over a placeholder.
      try {
        const { videoClient: v, chatClient: c } = await getStreamClients();
        if (!mounted) return;
        setVideoClient(v);
        setChatClient(c);
        const cl = v.call(s.call_type, s.call_id);
        callRef.current = cl;
        await cl.join({ create: false });
        if (!mounted) return;
        setCall(cl);
        const ch = c.channel("livestream", s.call_id, { name: s.title });
        await ch.watch();
        if (!mounted) return;
        setChannel(ch);
      } catch (e) {
        console.warn("Live A/V unavailable — overlay-only view", e);
      }
    })();
    return () => {
      mounted = false;
      if (callRef.current) callRef.current.leave().catch(() => {});
    };
  }, [sessionId, nav]);

  const sendGift = async (gift) => {
    try {
      const { data } = await api.post("/live/gift", { session_id: sessionId, gift_id: gift.id, quantity: multiplier });
      setRecentGift({ ...data.gift, frame: data.sender_badge_frame });
      setTimeout(() => setRecentGift(null), 3500);
      if (channel) {
        await channel.sendMessage({
          text: `🎁 sent a ${gift.label}${multiplier > 1 ? ` x${multiplier}` : ""}`,
          gift_id: gift.id,
          gift_label: gift.label,
          gift_emoji: gift.emoji,
          waves_spent: data.waves_spent,
        });
      }
      toast.success(`Sent ${gift.label}${multiplier > 1 ? ` x${multiplier}` : ""} (${data.waves_spent} waves)`);
      setGiftOpen(false);
      api.get(`/live/${sessionId}/gift-goal`).then((r) => setGoal(r.data)).catch(() => {});
      loadContrib();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send gift");
    }
  };

  if (!session) {
    return (
      <div className="min-h-[100dvh] bg-ink flex items-center justify-center text-cream/50 text-xs tracking-[0.3em] uppercase animate-pulse">
        Tuning in…
      </div>
    );
  }
  const streamReady = videoClient && call;

  return (
    <div className="h-[100dvh] bg-black relative overflow-hidden" data-testid="live-viewer">
      {/* Main livestream (best-effort A/V) */}
      {streamReady ? (
        <StreamVideo client={videoClient}>
          <StreamCall call={call}>
            <div className="absolute inset-0">
              <LivestreamLayout showParticipantCount={true} showDuration={true} showLiveBadge={true} />
            </div>
          </StreamCall>
        </StreamVideo>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-[#1a1410] to-black">
          <div className="text-center px-8">
            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse mx-auto mb-3" />
            <p className="text-cream/60 text-xs tracking-[0.3em] uppercase">Live video plays in the app</p>
          </div>
        </div>
      )}
          {/* Top bar — creator (left) · Follow (center) · viewers (right) */}
          <div className="absolute top-4 left-3 right-3 z-30 flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <button onClick={() => nav("/live")} className="w-9 h-9 shrink-0 rounded-full crystal-glass text-cream flex items-center justify-center" data-testid="viewer-back">
                <ArrowLeft size={16} />
              </button>
              <button onClick={() => nav(`/u/${session.host_username}`)} data-testid="live-creator" className="crystal-glass pl-1 pr-3 py-1 rounded-full flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 shrink-0 rounded-full overflow-hidden bg-white/10 flex items-center justify-center">
                  {session.host_avatar_path
                    ? <img src={fileUrl(session.host_avatar_path)} alt="" className="w-full h-full object-cover" />
                    : <span className="text-papaya text-sm font-black">{(session.host_display_name || session.host_username || "?")[0]?.toUpperCase()}</span>}
                </div>
                <div className="min-w-0 text-left">
                  <div className="flex items-center gap-1">
                    <span className="text-cream text-xs font-black truncate max-w-[120px]">{session.host_display_name || `@${session.host_username}`}</span>
                    <span className="bg-red-500 text-white text-[8px] font-black px-1.5 py-[1px] rounded-full tracking-wide">LIVE</span>
                  </div>
                  <span className="text-cream/50 text-[9px]">{fmt(followers)} followers</span>
                </div>
              </button>
            </div>

            {me?.id !== session.host_user_id && (
              <button
                onClick={followHost}
                data-testid="live-follow"
                className={`absolute left-1/2 -translate-x-1/2 px-5 py-2 rounded-full text-[11px] font-black tracking-[0.12em] uppercase transition-colors ${following ? "bg-white/10 text-cream/70 border border-white/15" : "bg-papaya text-ink papaya-glow"}`}
              >
                {following ? "Following" : "Follow"}
              </button>
            )}

            <button onClick={() => setViewersOpen(true)} data-testid="live-viewers-btn" className="crystal-glass px-3 py-2 rounded-full flex items-center gap-1.5 shrink-0">
              <Users size={16} weight="fill" className="text-cream" />
              <span className="text-cream text-xs font-black" data-testid="live-viewer-count">{fmt(viewerCount)}</span>
            </button>
          </div>

          {/* League + LIVE ranking chips (Phase 2) */}
          {rank && (
            <div className="absolute top-[60px] left-3 right-3 z-20 flex items-center gap-2" data-testid="live-rank-row">
              <button
                onClick={() => setViewersOpen(true)}
                data-testid="live-league-badge"
                className={`px-2.5 py-1 rounded-full border text-[11px] font-black tracking-[0.12em] uppercase flex items-center gap-1 ${leagueStyle(rank.league?.label)}`}
                title={`League ${rank.league?.label} · ${fmt(rank.league?.points)} pts`}
              >
                🏆 {rank.league?.label}
              </button>
              <div className="px-2.5 py-1 rounded-full crystal-glass text-[11px] font-black text-cream flex items-center gap-1.5" data-testid="live-ranking-pct">
                🔥 Top {rank.percentile}%
                <span className="text-cream/50">·</span>
                <span className="text-papaya">#{rank.rank}</span>
              </div>
            </div>
          )}

          {/* PK battle · gift battle · leaderboard · top-gifter entrance */}
          <div className="absolute top-[96px] left-4 right-4 z-20 space-y-2">
            <PkBattleBar sessionId={sessionId} myUserId={me?.id} isHost={me?.id === session.host_user_id} />
            <TopGifterEntrance sessionId={sessionId} />
            <GiftBattleOverlay sessionId={sessionId} isHost={false} />
            <GiftLeaderboardBanner sessionId={sessionId} />
          </div>
          <LiveGiftSplash sessionId={sessionId} isAdmin={me?.role === "admin"} />

          {/* Floating gift toast */}
          {recentGift && (
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 z-30 animate-fade-up pointer-events-none">
              <div className={`crystal-glass px-6 py-4 rounded-3xl text-center border ${frameStyle(recentGift.frame) ? `${frameStyle(recentGift.frame).border} ${frameStyle(recentGift.frame).glow}` : "border-transparent"}`} data-testid="viewer-gift-toast">
                {recentGift.image_url ? (
                  <img src={fileUrl(recentGift.image_url)} alt="" className="w-16 h-16 object-contain rounded-2xl mx-auto mb-2" />
                ) : (
                  <div className="text-5xl">{recentGift.emoji}</div>
                )}
                <p className={`text-sm font-black mt-1 ${frameStyle(recentGift.frame) ? "text-jam-gold" : "text-papaya"}`}>{recentGift.label}</p>
                {recentGift.designer_username && <p className="text-papaya text-[10px] font-black tracking-[0.15em] uppercase mt-1" data-testid="viewer-gift-designer">✦ designed by @{recentGift.designer_username}</p>}
              </div>
            </div>
          )}

          {/* Chat panel — bottom half */}
          {chatClient && channel && (
            <div className="absolute bottom-[88px] left-0 right-0 max-h-[40vh] z-20 pointer-events-none">
              <div className="pointer-events-auto bg-ink/40 backdrop-blur-md border-t border-white/10 max-h-[40vh] flex flex-col">
                <Chat client={chatClient} theme="str-chat__theme-dark">
                  <Channel channel={channel}>
                    <div className="h-[35vh] flex flex-col">
                      <div className="flex-1 overflow-hidden">
                        <MessageList Message={LiveChatMessage} />
                      </div>
                      <MessageComposer />
                    </div>
                  </Channel>
                </Chat>
              </div>
            </div>
          )}

          {/* Gift Goal progress bar */}
          {goal?.active && goal.goal && (
            <button
              onClick={() => setGoalOpen(true)}
              data-testid="live-goal-bar"
              className="absolute bottom-[96px] left-4 right-20 z-30 crystal-glass rounded-2xl px-3 py-2 text-left"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-cream text-[11px] font-black flex items-center gap-1 truncate">
                  <Target size={13} weight="fill" className="text-papaya shrink-0" />
                  {goal.goal.title || "Gift Goal"}
                </span>
                <span className="text-papaya text-[11px] font-black shrink-0">{fmt(goal.raised_waves)}/{fmt(goal.goal.target_waves)}</span>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-papaya rounded-full transition-all" style={{ width: `${goal.progress_pct}%` }} data-testid="live-goal-fill" />
              </div>
            </button>
          )}

          {/* Right action rail */}
          <div className="absolute bottom-4 right-4 z-30 flex flex-col items-center gap-3">
            <button onClick={() => nav("/wallet")} data-testid="live-get-coins" className="flex flex-col items-center gap-1" title="Get Coins">
              <span className="w-12 h-12 rounded-full crystal-glass flex items-center justify-center text-jam-gold"><Plus size={20} weight="bold" /></span>
              <span className="text-cream/70 text-[9px] font-bold tracking-wide">COINS</span>
            </button>
            <button onClick={() => setGalleryOpen(true)} data-testid="live-gallery-open" className="flex flex-col items-center gap-1" title="Gallery">
              <span className="w-12 h-12 rounded-full crystal-glass flex items-center justify-center text-cream"><Images size={20} weight="fill" /></span>
              <span className="text-cream/70 text-[9px] font-bold tracking-wide">GALLERY</span>
            </button>
            <button onClick={() => setContribOpen(true)} data-testid="live-contrib-open" className="flex flex-col items-center gap-1" title="My Contribution">
              <span className="w-12 h-12 rounded-full crystal-glass flex items-center justify-center text-cream"><ChartBar size={20} weight="fill" /></span>
              <span className="text-cream/70 text-[9px] font-bold tracking-wide">MINE</span>
            </button>
            <button onClick={() => setTitlesOpen(true)} data-testid="live-titles-open" className="flex flex-col items-center gap-1" title="Titles">
              <span className="w-12 h-12 rounded-full crystal-glass flex items-center justify-center text-jam-gold"><Medal size={20} weight="fill" /></span>
              <span className="text-cream/70 text-[9px] font-bold tracking-wide">TITLES</span>
            </button>
            <button
              onClick={() => setGiftOpen(true)}
              data-testid="viewer-gift-open"
              className="w-14 h-14 rounded-full bg-papaya text-ink flex items-center justify-center papaya-glow"
              title="Send a gift"
            >
              <Gift size={26} weight="fill" />
            </button>
          </div>

          {/* Gift picker sheet */}
          {giftOpen && (
            <div className="absolute inset-0 z-40 bg-ink/80 backdrop-blur-sm flex items-end" onClick={() => setGiftOpen(false)}>
              <div className="w-full bg-ink rounded-t-3xl p-5 max-h-[70vh] overflow-y-auto border-t border-white/10" onClick={(e) => e.stopPropagation()} data-testid="viewer-gift-panel">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-cream text-2xl font-black tracking-tighter">Send a gift</h3>
                  <button onClick={() => setGiftOpen(false)} className="text-cream/60 text-xs tracking-[0.2em] uppercase">Close</button>
                </div>
                {/* Title Multiplier + Get Coins */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2" data-testid="gift-multiplier">
                    {[1, 3].map((m) => (
                      <button
                        key={m}
                        onClick={() => setMultiplier(m)}
                        data-testid={`gift-multiplier-x${m}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-black tracking-wide transition-colors ${multiplier === m ? "bg-jam-gold text-jam-black" : "bg-white/10 text-cream/70"}`}
                      >
                        x{m}
                      </button>
                    ))}
                    <span className="text-cream/40 text-[10px] tracking-wide">Multiplier</span>
                  </div>
                  <button onClick={() => nav("/wallet")} data-testid="gift-get-coins" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-jam-gold/20 text-jam-gold text-xs font-black">
                    <Plus size={13} weight="bold" /> Get Coins
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {gifts.map((g) => (
                    <button
                      key={g.id}
                      onClick={() => sendGift(g)}
                      data-testid={`viewer-gift-${g.id}`}
                      className="rounded-2xl bg-white/5 border border-white/10 hover:border-papaya p-3 flex flex-col items-center transition-colors"
                    >
                      {g.is_community && (
                        <span className={`text-[8px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded-full mb-1 ${g.is_limited ? "bg-jam-gold text-jam-black" : "bg-emerald-400 text-ink"}`}>{g.badge || (g.is_limited ? "Limited" : "Community")}</span>
                      )}
                      {g.image_url ? (
                        <img src={fileUrl(g.image_url)} alt="" className="w-12 h-12 object-contain rounded-xl" style={g.custom ? { background: `linear-gradient(135deg, ${g.custom.colors?.bg1}, ${g.custom.colors?.bg2})` } : undefined} />
                      ) : (
                        <div className="text-3xl">{g.emoji}</div>
                      )}
                      <p className="text-cream text-[11px] font-bold mt-2 text-center leading-tight">{g.label}</p>
                      <p className="text-papaya text-[10px] tracking-[0.15em] uppercase font-black mt-1">{fmt(g.waves * multiplier)} W{multiplier > 1 ? ` ·x${multiplier}` : ""}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Viewers / Top Viewers half-sheet */}
          {viewersOpen && (
            <div className="absolute inset-0 z-40 bg-ink/80 backdrop-blur-sm flex items-end" onClick={() => setViewersOpen(false)}>
              <div className="w-full bg-ink rounded-t-3xl p-5 max-h-[62vh] overflow-y-auto border-t border-white/10" onClick={(e) => e.stopPropagation()} data-testid="live-viewers-sheet">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-cream text-2xl font-black tracking-tighter">Become a Top Viewer</h3>
                  <button onClick={() => setViewersOpen(false)} className="text-cream/60 text-xs tracking-[0.2em] uppercase">Close</button>
                </div>
                <p className="text-cream/50 text-xs mb-4">Get noticed by supporting this LIVE.</p>
                <div className="flex items-center gap-2 mb-4 text-cream/70 text-xs"><Users size={14} weight="fill" /> {fmt(viewerCount)} watching now</div>
                {topViewers.length ? (
                  <div className="space-y-2">
                    {topViewers.map((v) => (
                      <div key={v.user_id} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3" data-testid={`top-viewer-${v.rank}`}>
                        <span className="text-2xl w-8 text-center">{v.rank === 1 ? "🥇" : v.rank === 2 ? "🥈" : "🥉"}</span>
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 flex items-center justify-center">
                          {v.avatar_path ? <img src={fileUrl(v.avatar_path)} alt="" className="w-full h-full object-cover" /> : <span className="text-papaya font-black">{(v.username || "?")[0]?.toUpperCase()}</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-cream text-sm font-bold truncate">@{v.username}</p>
                          <p className="text-cream/50 text-[11px]">❤️ {v.hearts} · 🌊 {fmt(v.contribution_waves)} WAVE</p>
                        </div>
                        <span className="text-papaya text-xs font-black">#{v.rank}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl bg-white/5 border border-dashed border-white/15 p-6 text-center" data-testid="top-viewers-empty">
                    <p className="text-cream font-black">No top viewers yet</p>
                    <p className="text-cream/50 text-xs mt-1">You can be #1.</p>
                  </div>
                )}
                {me?.id !== session.host_user_id && (
                  <button onClick={sendWave} disabled={waveBusy} data-testid="live-send-wave" className="mt-5 w-full py-4 rounded-3xl bg-papaya text-ink font-black flex items-center justify-center gap-2 disabled:opacity-50">
                    <Heart size={20} weight="fill" /> {waveBusy ? "Sending…" : "Send · 1 WAVE"}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Gift Goal sheet */}
          {goalOpen && goal?.goal && (
            <div className="absolute inset-0 z-40 bg-ink/80 backdrop-blur-sm flex items-end" onClick={() => setGoalOpen(false)}>
              <div className="w-full bg-ink rounded-t-3xl p-5 max-h-[62vh] overflow-y-auto border-t border-white/10" onClick={(e) => e.stopPropagation()} data-testid="live-goal-sheet">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-cream text-2xl font-black tracking-tighter flex items-center gap-2"><Target size={22} weight="fill" className="text-papaya" /> {goal.goal.title || "Gift Goal"}</h3>
                  <button onClick={() => setGoalOpen(false)} className="text-cream/60 text-xs tracking-[0.2em] uppercase">Close</button>
                </div>
                <p className="text-cream/50 text-xs mb-4">Help this LIVE reach its goal.</p>
                <div className="flex items-end justify-between mb-1">
                  <span className="text-cream text-3xl font-black">{fmt(goal.raised_waves)}<span className="text-cream/40 text-lg"> / {fmt(goal.goal.target_waves)}</span></span>
                  <span className="text-papaya text-xl font-black">{goal.progress_pct}%</span>
                </div>
                <div className="h-3 rounded-full bg-white/10 overflow-hidden mb-2">
                  <div className="h-full bg-papaya rounded-full transition-all" style={{ width: `${goal.progress_pct}%` }} />
                </div>
                <div className="flex items-center justify-between text-cream/50 text-[11px] mb-4">
                  <span>{goal.contributors_count} contributor{goal.contributors_count === 1 ? "" : "s"}</span>
                  {goal.time_left != null && <span>⏱ {Math.floor(goal.time_left / 60)}m {goal.time_left % 60}s left</span>}
                  {goal.reached && <span className="text-emerald-400 font-black">Goal reached! 🎉</span>}
                </div>
                <p className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold mb-2">Top contributors</p>
                {goal.top_contributors.length ? (
                  <div className="space-y-2">
                    {goal.top_contributors.map((c) => (
                      <div key={c.user_id} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3" data-testid={`goal-contributor-${c.rank}`}>
                        <span className="text-xl w-7 text-center">{c.rank === 1 ? "🥇" : c.rank === 2 ? "🥈" : "🥉"}</span>
                        <div className="w-9 h-9 rounded-full overflow-hidden bg-white/10 flex items-center justify-center">
                          {c.avatar_path ? <img src={fileUrl(c.avatar_path)} alt="" className="w-full h-full object-cover" /> : <span className="text-papaya font-black text-sm">{(c.username || "?")[0]?.toUpperCase()}</span>}
                        </div>
                        <p className="text-cream text-sm font-bold flex-1 truncate">@{c.username}</p>
                        <span className="text-papaya text-xs font-black">{fmt(c.waves)} W</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl bg-white/5 border border-dashed border-white/15 p-5 text-center">
                    <p className="text-cream/60 text-xs">Be the first to contribute.</p>
                  </div>
                )}
                {gallery.length > 0 && (
                  <div className="mt-5">
                    <p className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold mb-2">Goal gallery</p>
                    <div className="space-y-2">
                      {gallery.map((g) => (
                        <div key={g.id} data-testid={`goal-gallery-${g.id}`} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-cream text-sm font-bold truncate">{g.title || "Gift Goal"}</p>
                            <p className="text-cream/50 text-[11px] truncate">{fmt(g.raised_waves)}/{fmt(g.target_waves)} W · {g.contributors_count} contrib{g.title_winner ? ` · 🏆 @${g.title_winner.username}` : ""}</p>
                          </div>
                          <span className={`text-[10px] font-black uppercase tracking-wide ${g.status === "completed" ? "text-emerald-400" : g.status === "expired" ? "text-cream/40" : "text-jam-gold"}`}>{g.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {me?.id !== session.host_user_id && (
                  <button onClick={() => { setGoalOpen(false); setGiftOpen(true); }} className="mt-5 w-full py-4 rounded-3xl bg-papaya text-ink font-black flex items-center justify-center gap-2" data-testid="goal-send-gift">
                    <Gift size={18} weight="fill" /> Contribute a gift
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Gallery sheet — host's recent posts */}
          {galleryOpen && (
            <div className="absolute inset-0 z-40 bg-ink/80 backdrop-blur-sm flex items-end" onClick={() => setGalleryOpen(false)}>
              <div className="w-full bg-ink rounded-t-3xl p-5 max-h-[70vh] overflow-y-auto border-t border-white/10" onClick={(e) => e.stopPropagation()} data-testid="live-gallery-sheet">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-cream text-2xl font-black tracking-tighter">@{session.host_username}'s gallery</h3>
                  <button onClick={() => setGalleryOpen(false)} className="text-cream/60 text-xs tracking-[0.2em] uppercase">Close</button>
                </div>
                {galleryPosts.length ? (
                  <div className="grid grid-cols-3 gap-2">
                    {galleryPosts.map((p) => {
                      const thumb = p.media_type === "video" && p.mux_playback_id
                        ? `https://image.mux.com/${p.mux_playback_id}/thumbnail.jpg?width=300`
                        : (p.media_type === "image" ? fileUrl(p.media_url) : null);
                      return (
                        <button key={p.id} onClick={() => nav(`/p/${p.id}`)} data-testid={`gallery-post-${p.id}`} className="aspect-[3/4] rounded-xl overflow-hidden bg-white/5 border border-white/10 relative">
                          {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" /> : (
                            <div className="w-full h-full flex items-center justify-center p-2"><span className="text-cream/60 text-[10px] text-center line-clamp-3">{p.caption || "Post"}</span></div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-2xl bg-white/5 border border-dashed border-white/15 p-8 text-center" data-testid="gallery-empty">
                    <div className="text-3xl mb-2">🎞️</div>
                    <p className="text-cream font-black">No posts yet</p>
                    <p className="text-cream/50 text-xs mt-1">This creator hasn't posted content.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* My Contribution sheet */}
          {contribOpen && (
            <div className="absolute inset-0 z-40 bg-ink/80 backdrop-blur-sm flex items-end" onClick={() => setContribOpen(false)}>
              <div className="w-full bg-ink rounded-t-3xl p-5 max-h-[62vh] overflow-y-auto border-t border-white/10" onClick={(e) => e.stopPropagation()} data-testid="live-contrib-sheet">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-cream text-2xl font-black tracking-tighter">My contribution</h3>
                  <button onClick={() => setContribOpen(false)} className="text-cream/60 text-xs tracking-[0.2em] uppercase">Close</button>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-3 text-center">
                    <p className="text-cream text-2xl font-black" data-testid="contrib-waves">{fmt(contrib?.waves_total || 0)}</p>
                    <p className="text-cream/50 text-[10px] tracking-widest uppercase mt-1">Waves</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-3 text-center">
                    <p className="text-cream text-2xl font-black">{fmt(contrib?.gift_count || 0)}</p>
                    <p className="text-cream/50 text-[10px] tracking-widest uppercase mt-1">Gifts</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-3 text-center">
                    <p className="text-papaya text-2xl font-black" data-testid="contrib-rank">{contrib?.rank ? `#${contrib.rank}` : "—"}</p>
                    <p className="text-cream/50 text-[10px] tracking-widest uppercase mt-1">Rank</p>
                  </div>
                </div>
                {contrib?.is_top_viewer && <div className="mb-4 rounded-2xl bg-jam-gold/15 border border-jam-gold/30 p-3 text-center text-jam-gold text-xs font-black">🏆 You're a Top Viewer of this LIVE!</div>}
                <p className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold mb-2">Recent</p>
                {contrib?.recent?.length ? (
                  <div className="space-y-2">
                    {contrib.recent.map((g, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3">
                        <span className="text-xl">{g.gift_emoji || "🎁"}</span>
                        <p className="text-cream text-sm font-bold flex-1 truncate">{g.gift_name || "Gift"}{g.quantity > 1 ? ` x${g.quantity}` : ""}</p>
                        <span className="text-papaya text-xs font-black">{fmt(g.waves_spent)} W</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl bg-white/5 border border-dashed border-white/15 p-6 text-center" data-testid="contrib-empty">
                    <p className="text-cream font-black">No contributions yet</p>
                    <p className="text-cream/50 text-xs mt-1">Send a gift or a WAVE to climb the ranks.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <TitlesSheet
            sessionId={sessionId}
            open={titlesOpen}
            onClose={() => setTitlesOpen(false)}
            onGetTitle={me?.id !== session.host_user_id ? () => setGiftOpen(true) : undefined}
          />
        </div>
  );
}
